import { useState, useEffect, useRef } from "react";
import { T } from "../utils";

export const FontLink = () => (
  <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,600&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap" rel="stylesheet"/>
);

export const Badge = ({ label, color, bg, small=false }) => (
  <span style={{ display:"inline-block", padding:small?"1px 7px":"2px 10px", borderRadius:20, fontSize:small?10:11, fontWeight:600, color, background:bg, letterSpacing:"0.3px", whiteSpace:"nowrap" }}>
    {label}
  </span>
);

export const TypeBadge = ({ type }) => {
  const cfg = { cnom:{label:"🔵 CNOM Member",color:"#1e5fa8",bg:"#eff6ff"}, clarion:{label:"🟡 Clarion Call",color:"#c9972a",bg:"#fdf3dc"}, occasional:{label:"⚪ Occasional",color:"#6b6560",bg:"#eeece8"}, visitor:{label:"👋 New Visitor",color:"#1a6b3c",bg:"#eaf4ef"} }[type];
  if (!cfg) return null;
  return <Badge label={cfg.label} color={cfg.color} bg={cfg.bg} small/>;
};

export const Avatar = ({ name, size=32, src, style:sx={} }) => {
  if (src) {
    return (
      <img src={src} alt={name||""} style={{ width:size, height:size, borderRadius:"50%", objectFit:"cover", flexShrink:0, ...sx }}/>
    );
  }
  const cols = ["#1a3460","#6d4c41","#2e7d32","#1565c0","#6a1b9a","#ad1457","#00695c","#e65100"];
  const bg   = cols[(name||"A").charCodeAt(0) % cols.length];
  const ini  = (name||"?").split(" ").map(x=>x[0]).join("").slice(0,2).toUpperCase();
  return (
    <div style={{ width:size, height:size, borderRadius:"50%", background:bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*0.38, fontWeight:700, color:"#fff", flexShrink:0, ...sx }}>
      {ini}
    </div>
  );
};

export const Btn = ({ children, onClick, color=T.navy, outline=false, small=false, disabled=false, danger=false, style:sx={} }) => {
  const bg = danger ? T.red : outline ? "transparent" : color;
  const cl = danger ? "#fff" : outline ? color : "#fff";
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ display:"inline-flex", alignItems:"center", gap:6, padding:small?"6px 12px":"9px 18px", borderRadius:7, fontSize:small?12:13, fontWeight:600, cursor:disabled?"not-allowed":"pointer", opacity:disabled?0.5:1, border:outline||danger?`1.5px solid ${danger?T.red:color}`:"none", background:bg, color:cl, fontFamily:"'DM Sans',sans-serif", transition:"all 0.15s", ...sx }}>
      {children}
    </button>
  );
};

export const Input = ({ label, value, onChange, type="text", placeholder="", required=false, hint="", disabled=false }) => (
  <div style={{ marginBottom:14 }}>
    {label && <div style={{ fontSize:12, fontWeight:600, color:T.grey600, marginBottom:5 }}>{label}{required&&<span style={{color:T.red}}> *</span>}</div>}
    <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} disabled={disabled}
      style={{ width:"100%", boxSizing:"border-box", padding:"9px 12px", borderRadius:7, border:`1.5px solid ${T.grey200}`, fontSize:13, fontFamily:"'DM Sans',sans-serif", color:T.grey800, outline:"none", background:disabled?T.grey50:T.white }}/>
    {hint && <div style={{ fontSize:11, color:T.grey400, marginTop:3 }}>{hint}</div>}
  </div>
);

export const Textarea = ({ label, value, onChange, placeholder="", rows=4, required=false }) => (
  <div style={{ marginBottom:14 }}>
    {label && <div style={{ fontSize:12, fontWeight:600, color:T.grey600, marginBottom:5 }}>{label}{required&&<span style={{color:T.red}}> *</span>}</div>}
    <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows}
      style={{ width:"100%", boxSizing:"border-box", padding:"9px 12px", borderRadius:7, border:`1.5px solid ${T.grey200}`, fontSize:13, fontFamily:"'DM Sans',sans-serif", color:T.grey800, outline:"none", resize:"vertical" }}/>
  </div>
);

export const Select = ({ label, value, onChange, options, required=false, disabled=false }) => (
  <div style={{ marginBottom:14 }}>
    {label && <div style={{ fontSize:12, fontWeight:600, color:T.grey600, marginBottom:5 }}>{label}{required&&<span style={{color:T.red}}> *</span>}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)} disabled={disabled}
      style={{ width:"100%", padding:"9px 12px", borderRadius:7, border:`1.5px solid ${T.grey200}`, fontSize:13, fontFamily:"'DM Sans',sans-serif", color:T.grey800, outline:"none", background:T.white }}>
      {options.map(o => <option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
    </select>
  </div>
);

export const Card = ({ children, style:sx={}, onClick, onMouseEnter, onMouseLeave }) => (
  <div
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{ background:T.white, border:`1px solid ${T.grey200}`, borderRadius:12, padding:"22px 24px", boxShadow:"0 1px 4px rgba(0,0,0,0.07)", ...sx }}>
    {children}
  </div>
);

export const PageHeader = ({ title, subtitle, action }) => (
  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28 }}>
    <div>
      <div style={{ fontSize:22, fontWeight:700, color:T.navy, fontFamily:"'Playfair Display',serif" }}>{title}</div>
      {subtitle && <div style={{ fontSize:13, color:T.grey600, marginTop:3 }}>{subtitle}</div>}
    </div>
    {action}
  </div>
);

export const SectionTitle = ({ children, style:sx={} }) => (
  <div style={{ fontSize:15, fontWeight:700, color:T.navy, marginBottom:16, fontFamily:"'Playfair Display',serif", ...sx }}>
    {children}
  </div>
);

export const StatCard = ({ label, value, sub, color, icon }) => (
  <div style={{ background:T.white, border:`1px solid ${T.grey200}`, borderRadius:12, padding:"18px 20px", boxShadow:"0 1px 4px rgba(0,0,0,0.06)", borderLeft:`4px solid ${color}` }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
      <div>
        <div style={{ fontSize:24, fontWeight:700, color:T.navy, fontFamily:"'Playfair Display',serif" }}>{value}</div>
        <div style={{ fontSize:12, fontWeight:700, color:T.grey600, marginTop:2, textTransform:"uppercase", letterSpacing:"0.4px" }}>{label}</div>
        {sub && <div style={{ fontSize:11, color, marginTop:4, fontWeight:600 }}>{sub}</div>}
      </div>
      {icon && <div style={{ fontSize:26 }}>{icon}</div>}
    </div>
  </div>
);

export const Modal = ({ open, onClose, title, children, width=520 }) => {
  if (!open) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }} onClick={onClose}>
      <div style={{ background:T.white, border:`1px solid ${T.grey200}`, borderRadius:14, padding:28, width, maxWidth:"94vw", maxHeight:"88vh", overflowY:"auto", boxShadow:"0 24px 80px rgba(0,0,0,0.6)" }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div style={{ fontSize:17, fontWeight:700, color:T.navy, fontFamily:"'Playfair Display',serif" }}>{title}</div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:20, color:T.grey400 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const Th = ({ children }) => (
  <th style={{ textAlign:"left", padding:"10px 14px", fontSize:11, fontWeight:700, color:T.grey600, textTransform:"uppercase", letterSpacing:"0.6px", borderBottom:`2px solid ${T.grey100}`, whiteSpace:"nowrap" }}>
    {children}
  </th>
);

export const Td = ({ children, style:sx={} }) => (
  <td style={{ padding:"11px 14px", fontSize:13, borderBottom:`1px solid ${T.grey50}`, verticalAlign:"middle", color:T.grey800, ...sx }}>
    {children}
  </td>
);

export const Spinner = ({ label="Loading..." }) => (
  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:60, flexDirection:"column", gap:12 }}>
    <img src="/logo192.png" alt="CCN" style={{ width:48, height:48, objectFit:"contain", marginBottom:4 }}/>
    <div style={{ width:36, height:36, border:`3px solid ${T.grey200}`, borderTop:`3px solid ${T.gold}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <div style={{ fontSize:13, color:T.grey400 }}>{label}</div>
  </div>
);

export const EmptyState = ({ icon="📭", title, subtitle, action }) => (
  <div style={{ textAlign:"center", padding:"48px 24px", color:T.grey400 }}>
    <div style={{ fontSize:40, marginBottom:12 }}>{icon}</div>
    <div style={{ fontSize:15, fontWeight:600, color:T.grey600 }}>{title}</div>
    {subtitle && <div style={{ fontSize:13, marginTop:4 }}>{subtitle}</div>}
    {action && <div style={{ marginTop:16 }}>{action}</div>}
  </div>
);

export const AccessDenied = () => (
  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"80px 24px", textAlign:"center" }}>
    <div style={{ fontSize:48, marginBottom:16 }}>🔒</div>
    <div style={{ fontSize:20, fontWeight:700, color:T.navy, fontFamily:"'Playfair Display',serif", marginBottom:8 }}>Access Restricted</div>
    <div style={{ fontSize:14, color:T.grey600, maxWidth:360 }}>You don't have permission to view this section. Contact your administrator if you need access.</div>
  </div>
);

export const VoicePlayer = ({ text }) => {
  const [playing, setPlaying] = useState(false);
  const resumeRef = useRef(null);

  // Cancel speech and clear resume interval on unmount
  useEffect(() => {
    return () => {
      clearInterval(resumeRef.current);
      window.speechSynthesis.cancel();
    };
  }, []);

  // When text changes (new lesson/day/sermon loaded), stop any in-progress speech
  useEffect(() => {
    clearInterval(resumeRef.current);
    window.speechSynthesis.cancel();
    setPlaying(false);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!('speechSynthesis' in window) || !text) return null;
  const stripped = text.replace(/<[^>]+>/g, ' ').replace(/&[a-z]+;/gi, ' ').replace(/\s+/g, ' ').trim();
  if (!stripped) return null;

  const play = () => {
    clearInterval(resumeRef.current);
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(stripped);
    utt.lang = 'en-GB';
    utt.rate = 0.92;
    utt.pitch = 1.05;
    // Prefer a natural-sounding voice: Google UK, Microsoft, or any non-default
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v => /google uk english female/i.test(v.name))
      || voices.find(v => /google uk english/i.test(v.name))
      || voices.find(v => /microsoft.*english/i.test(v.name))
      || voices.find(v => v.lang === 'en-GB' && !v.default)
      || voices.find(v => v.lang.startsWith('en') && !v.default);
    if (preferred) utt.voice = preferred;
    utt.onend  = () => { clearInterval(resumeRef.current); setPlaying(false); };
    utt.onerror = () => { clearInterval(resumeRef.current); setPlaying(false); };
    window.speechSynthesis.speak(utt);
    setPlaying(true);
    // Chrome pauses long utterances after ~15s — resume every 10s if paused
    resumeRef.current = setInterval(() => {
      if (window.speechSynthesis.paused) window.speechSynthesis.resume();
    }, 10000);
  };

  const stop = () => {
    clearInterval(resumeRef.current);
    window.speechSynthesis.cancel();
    setPlaying(false);
  };

  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 14px", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, marginBottom:14 }}>
      <span style={{ fontSize:16 }}>🔊</span>
      <span style={{ fontSize:12, color:"#1e40af", fontWeight:600, flex:1 }}>
        {playing ? "Reading aloud..." : "Listen to this content"}
      </span>
      {!playing
        ? <button onClick={play}  style={{ padding:"4px 14px", background:"#1e40af", color:"#fff", border:"none", borderRadius:6, fontSize:12, fontWeight:600, cursor:"pointer" }}>▶ Play</button>
        : <button onClick={stop}  style={{ padding:"4px 14px", background:"#dc2626", color:"#fff", border:"none", borderRadius:6, fontSize:12, fontWeight:600, cursor:"pointer" }}>■ Stop</button>
      }
    </div>
  );
};

export const TypeSelector = ({ selected, onChange }) => {
  const types = { cnom:{label:"CNOM Member",color:"#1e5fa8",bg:"#eff6ff",icon:"🔵"}, clarion:{label:"Clarion Call",color:"#c9972a",bg:"#fdf3dc",icon:"🟡"}, occasional:{label:"Occasional Visitor",color:"#6b6560",bg:"#eeece8",icon:"⚪"}, visitor:{label:"New Visitor",color:"#1a6b3c",bg:"#eaf4ef",icon:"👋"} };
  const toggle = key => onChange(selected.includes(key) ? selected.filter(x=>x!==key) : [...selected, key]);
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:12, fontWeight:600, color:T.grey600, marginBottom:8 }}>PERSON TYPE <span style={{color:T.red}}>*</span> <span style={{color:T.grey400,fontWeight:400}}>(select all that apply)</span></div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
        {Object.entries(types).map(([key,cfg]) => (
          <div key={key} onClick={()=>toggle(key)}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:20, cursor:"pointer", border:`1.5px solid ${selected.includes(key)?cfg.color:T.grey200}`, background:selected.includes(key)?cfg.bg:T.white, fontSize:13, fontWeight:selected.includes(key)?700:400, color:selected.includes(key)?cfg.color:T.grey600, transition:"all 0.15s" }}>
            {selected.includes(key) && <span style={{fontSize:12}}>✓</span>}
            {cfg.icon} {cfg.label}
          </div>
        ))}
      </div>
    </div>
  );
};
